import { useLicence } from '@/services/queries';
import { StatusPill } from '@/shared/ui/status-pill';
import { Skeleton } from '@/shared/ui/skeleton';

/**
 * The caller's own seat and the machine it is bound to.
 *
 * Transcribed from the design's right-hand column: a 16px card at 24px/26px,
 * label-and-value rows 12px apart with the value in mono, then a hairline and
 * a note explaining why none of it is editable.
 *
 * ── Why read-only, and why the note says so ─────────────────────────────────
 *
 * The design labels this "Read-only" and spells out that seat reassignment goes
 * through NuDent support. That is not a placeholder for a missing feature - it
 * is the licensing model. A seat binds to a device fingerprint, and letting a
 * customer rebind their own seat at will is letting them run one licence on any
 * number of machines in sequence. So the note is content, not an apology, and
 * it belongs on the screen rather than in a FAQ nobody reads first.
 */

/** "22 Aug 2026", or a dash. Never a raw ISO string. */
function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-right">{children}</span>
    </div>
  );
}

/**
 * The two modes `license_mode` actually has.
 *
 * Only these two exist - there is no enterprise tier in the schema - so the
 * fallback below is for a mode added later, not for a case that exists now.
 */
const MODE_LABEL: Record<string, string> = {
  single_seat: 'Single Seat',
  lab_seat: 'Lab Seat',
};

export function SeatCard() {
  const query = useLicence();

  if (query.isPending) return <Skeleton className="h-64 w-full rounded-2xl" />;

  const licence = query.data?.licence ?? null;
  const seat = licence?.seats.find((s) => s.isYou);

  if (!licence || !seat) {
    return (
      <div className="rounded-2xl border border-border bg-card px-[26px] py-6 shadow-[0_1px_3px_rgba(15,23,42,0.05)]">
        <h2 className="mb-3.5 text-[13.5px] font-semibold">Your seat</h2>
        <p className="text-xs leading-[1.6] text-muted-foreground">
          No seat is assigned to you. Somebody who administers the lab without designing does not
          need one - ask your lab owner if you expected to have one.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card px-[26px] py-6 shadow-[0_1px_3px_rgba(15,23,42,0.05)]">
      <h2 className="mb-3.5 text-[13.5px] font-semibold">Your seat</h2>

      <div className="flex flex-col gap-3">
        <Row label="Licence">
          <span className="font-mono text-xs font-medium">
            {MODE_LABEL[licence.mode] ?? licence.mode}
          </span>
        </Row>
        <Row label="Workstation">
          <span className="font-mono text-xs font-medium">
            {seat.deviceLabel ? `WKS-${seat.deviceLabel.slice(0, 4)}` : 'not bound'}
          </span>
        </Row>
        <Row label="Fingerprint">
          {/*
            The pill says whether the machine is recognised; the hash underneath
            says which machine. Both, because "Active" alone does not let
            somebody on the phone to support identify the workstation.
          */}
          <StatusPill status={seat.status} />
        </Row>
        <Row label="Hash">
          <span className="font-mono text-[11.5px] text-muted-foreground">
            {seat.deviceLabel ? `${seat.deviceLabel}…` : '—'}
          </span>
        </Row>
        <Row label="Activated">
          <span className="font-mono text-xs font-medium">{formatDate(seat.activatedAt)}</span>
        </Row>
        <Row label="Licence ends">
          <span className="font-mono text-xs font-semibold">{formatDate(seat.expiresAt)}</span>
        </Row>
      </div>

      <p className="mt-3.5 border-t border-border pt-3.5 text-[11px] leading-[1.6] text-muted-foreground">
        Read-only. To move this seat to another workstation, contact NuDent support - a seat binds
        to one machine, and rebinding is what stops one licence being used on several in turn.
      </p>
    </div>
  );
}
